import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { LogIn, Eye, EyeOff } from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const from = location.state?.from?.pathname || "/home";

  // Check both state and localStorage for immediate auth check
  const hasToken = !!localStorage.getItem("b2b_token");
  const effectiveAuth = isAuthenticated || hasToken;

  React.useEffect(() => {
    if (effectiveAuth) {
      navigate(from, { replace: true });
    }
  }, [effectiveAuth, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success("Welcome back to Board2Boston™!");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: "radial-gradient(ellipse at center, rgba(122, 98, 0, 0.1) 0%, #050505 70%)"
      }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_hoop-invite/artifacts/r8lcaocz_Board2Boston%20favicon.png"
            alt="Board2Boston"
            className="h-20 mx-auto mb-4"
          />
          <h1 className="font-bebas text-4xl tracking-wider text-white">
            WELCOME BACK
          </h1>
          <p className="text-[#A1A1AA] font-ubuntu mt-2">
            Sign in to continue to Board2Boston™
          </p>
        </div>

        <Card className="bg-[#0A0A0A] border border-white/10">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-dark h-12"
                  required
                  data-testid="login-email"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-dark h-12 pr-12"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="btn-gold w-full h-14 text-lg"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    SIGNING IN...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-5 h-5" />
                    SIGN IN
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-[#A1A1AA] font-ubuntu">
                Don't have an account?{" "}
                <Link to="/register" className="text-[#7a6200] hover:text-[#C6A300] transition-colors">
                  Create Account
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer links */}
        <div className="mt-6 text-center space-y-2">
          <Link 
            to="/direct" 
            className="text-[#52525B] text-sm font-ubuntu hover:text-[#A1A1AA] transition-colors block"
          >
            Admin Access
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
